import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Receipt, ScanLine, Trash2 } from "lucide-react";
import { toast } from "react-toastify";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import Dropdown from "src/components/ui/Dropdown";
import FilterSelect from "src/components/ui/FilterSelect";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";
import Button from "src/components/ui/Button";
import InfoIcon from "src/components/ui/InfoIcon";
import AddCustomerModal from "src/components/modals/AddCustomerModal";
import ScanProductModal from "src/components/modals/ScanProductModal";
import CheckoutPaymentModal from "src/components/payments/CheckoutPaymentModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as salesActions from "src/redux/slices/salesSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { ProductCartItem } from "src/types/sales.types";
import { currencyFormatter, formatVariantLabel } from "src/utils/common";
import productApis from "src/api/product";
import stockApis from "src/api/stock";
import { Customer } from "src/types/customer.types";
import customerApis from "src/api/customer";
import salesApis from "src/api/salesOrder";
import { Product } from "src/types/product.types";

interface LineItemRow extends ProductCartItem {
  key: number;
  product: Product | null;
  productLabel: string;
  available: number | null;
}

const InfoRow: React.FC<{ label: string; value: string; span?: boolean }> = ({
  label,
  value,
  span = false,
}) => (
  <div className={span ? "sm:col-span-2" : undefined}>
    <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {label}
    </p>
    <p className="text-sm text-gray-800 dark:text-gray-100">{value}</p>
  </div>
);

const todayIso = () => new Date().toISOString().slice(0, 10);

let rowKeySeq = 0;
const emptyRow = (): LineItemRow => ({
  key: rowKeySeq++,
  product: null,
  productLabel: "",
  productId: "",
  variantId: "",
  name: "",
  mrp: 0,
  sellingPrice: 0,
  discountPercent: 0,
  taxPercent: 0,
  quantity: 1,
  totalPrice: 0,
  available: null,
});

const SalesForm: React.FC = () => {
  const { saleId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const { createSaleOrder, editSaleOrder } = useAppSelector(
    (state) => state.sales
  );
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel } = useWarehouseLabel();

  // ------------------ Warehouse ------------------
  const [warehouseId, setWarehouseId] = useState("");
  const [orderDate, setOrderDate] = useState(todayIso());

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  // ------------------ Customer ------------------
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerLabel, setCustomerLabel] = useState("");
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerSearched, setCustomerSearched] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  // ------------------ Line items ------------------
  const [rows, setRows] = useState<LineItemRow[]>([emptyRow()]);
  const [scanOpen, setScanOpen] = useState(false);
  const [highlightedRowKey, setHighlightedRowKey] = useState<number | null>(null);

  // ------------------ Summary ------------------
  const [discountPercentage, setDiscountPercentage] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState("cash");
  // Set once an Online order is saved — opens Collect payment for it.
  const [checkoutOrder, setCheckoutOrder] = useState<{
    _id: string;
    orderId: string;
    balanceDue: number;
  } | null>(null);
  const [remarks, setRemarks] = useState("");
  const [touched, setTouched] = useState(false);

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------ Effects ------------------
  useEffect(() => {
    const loadOrderDetails = async () => {
      if (!saleId) return;
      try {
        setIsLoadingDetails(true);
        const res = await salesApis.getOrderById(saleId);
        const order = res?.data;

        if (!order) {
          toast.error("Sale order not found");
          navigate("/app/sale/list");
          return;
        }
        const createdById =
          typeof order?.createdBy === "string"
            ? order.createdBy
            : order?.createdBy?._id;
        if (
          order?.orderStatus === "pending" &&
          !isTenantAdmin &&
          createdById &&
          user?._id &&
          String(createdById) !== String(user._id)
        ) {
          toast.error("Only the creator can edit this order");
          navigate("/app/sale/list");
          return;
        }
        if (order?.orderStatus !== "pending") {
          toast.error("Only pending sale orders can be edited");
          navigate("/app/sale/list");
          return;
        }

        setWarehouseId(order?.warehouseId?._id || order?.warehouseId || "");
        setOrderDate((order?.orderDate || order?.createdAt || todayIso()).slice(0, 10));

        const orderCustomer = order.customerId || {};
        setCustomer(orderCustomer);
        setCustomerLabel(
          orderCustomer.name
            ? `${orderCustomer.name} (${orderCustomer.mobile || "-"})`
            : ""
        );

        setPaymentMode(order.paymentMode || "cash");
        setRemarks(order.remarks || "");

        if (order.subTotal && order.discount) {
          const discountPercent = (order.discount / order.subTotal) * 100;
          setDiscountPercentage(discountPercent.toFixed(2));
        }

        const mappedRows: LineItemRow[] = (order.items || []).map(
          (item: any) => {
            const product: Product | null =
              item.productId && typeof item.productId === "object"
                ? item.productId
                : null;
            const sellingPrice = Number(item.sellingPrice || 0);
            const quantity = Number(item.quantity || 0);
            const discountPercent = Number(item.discountPercent || 0);
            const totalPrice =
              item.totalPrice ??
              Number(
                (quantity * sellingPrice * (1 - discountPercent / 100)).toFixed(2)
              );

            return {
              key: rowKeySeq++,
              product,
              productLabel: product
                ? `${product.name} (${product.sku || product.productId})`
                : item.name || "",
              productId:
                typeof item.productId === "string"
                  ? item.productId
                  : item.productId?._id || "",
              variantId: item.variantId || "",
              name: product?.name || item.name || "",
              mrp: Number(item.mrp || sellingPrice || 0),
              sellingPrice,
              discountPercent,
              taxPercent: Number(item.taxPercent ?? product?.gstRate ?? 18),
              quantity,
              totalPrice,
              available: null,
            };
          }
        );
        setRows(mappedRows.length ? mappedRows : [emptyRow()]);
      } catch (error) {
        console.error("Error fetching order details:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    loadOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId, navigate, isTenantAdmin, user?._id]);

  const handleClose = () => {
    dispatch(salesActions.resetCreateSalesOrder());
    dispatch(salesActions.resetEditSalesOrder());
    navigate("/app/sale/list");
  };

  useEffect(() => {
    if (createSaleOrder.data) {
      const createdOrder = (createSaleOrder.data as any)?.data;
      if (createdOrder?._id && createdOrder.paymentMode === "online") {
        // Order is saved (stock reserved) — now take the money.
        dispatch(salesActions.resetCreateSalesOrder());
        setCheckoutOrder({
          _id: createdOrder._id,
          orderId: createdOrder.orderId,
          balanceDue: createdOrder.balanceDue ?? createdOrder.grandTotal,
        });
        return;
      }
      toast.success("Order placed");
      handleClose();
    } else if (createSaleOrder.error) {
      toast.error(`Error: ${createSaleOrder.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSaleOrder]);

  useEffect(() => {
    if (editSaleOrder.data) {
      toast.success("Sale order updated successfully!");
      handleClose();
    } else if (editSaleOrder.error) {
      toast.error(`Error: ${editSaleOrder.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSaleOrder]);

  // ------------------ Availability ------------------
  const fetchAvailability = async (
    key: number,
    productId: string,
    variantId?: string
  ) => {
    if (!warehouseId || !productId) return;
    try {
      const res = await stockApis.getStockLevels({
        warehouseId,
        productId,
        ...(variantId ? { variantId } : {}),
      });
      const level = (res?.data?.data || res?.data || [])[0];
      const available = level ? Number(level.available ?? 0) : 0;
      setRows((prev) =>
        prev.map((r) => (r.key === key ? { ...r, available } : r))
      );
    } catch (error) {
      console.error("Failed to fetch availability", error);
    }
  };

  // Re-check availability for every row already carrying a product whenever
  // the warehouse changes.
  useEffect(() => {
    if (!warehouseId) return;
    rows.forEach((row) => {
      if (row.product?._id) fetchAvailability(row.key, row.product._id, row.variantId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  // ------------------ Customer Lookup ------------------
  const handleCustomerSearch = async (query: string) => {
    setCustomerSearching(true);
    try {
      const res = await customerApis.getCustomersList({
        search: query,
        page: 1,
        limit: 10,
      });
      const list: Customer[] = res?.data || [];
      setCustomerSearched(true);
      return list.map((c) => ({
        value: c._id || "",
        label: `${c.name} (${c.mobile})`,
        data: c,
      }));
    } catch (error) {
      console.error("Customer search failed", error);
      return [];
    } finally {
      setCustomerSearching(false);
    }
  };

  const handleSelectCustomer = (option: SearchInputOption<Customer>) => {
    setCustomer(option.data);
    setCustomerLabel(option.label);
  };

  const handleChangeCustomer = () => {
    setCustomer(null);
    setCustomerLabel("");
    setCustomerSearched(false);
  };

  const handleCustomerCreated = (created?: Customer) => {
    setShowAddCustomerModal(false);
    if (created) {
      setCustomer(created);
      setCustomerLabel(`${created.name} (${created.mobile})`);
    }
  };

  // ------------------ Row logic ------------------
  const updateRow = (key: number, patch: Partial<LineItemRow>) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const updated = { ...r, ...patch };
        const qty = parseFloat(String(updated.quantity)) || 0;
        const total = qty * updated.sellingPrice * (1 - (updated.discountPercent || 0) / 100);
        updated.totalPrice = Number(total.toFixed(2));
        return updated;
      })
    );
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (key: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const handleSelectProduct = (key: number, product: Product) => {
    // For variant products, "already added" is only meaningful once a
    // specific variant is chosen (checked in handleSelectVariant) — the
    // same product can legitimately appear in multiple rows with different
    // variants (e.g. Size S in one line, Size M in another).
    if (
      product.productType !== "variant" &&
      rows.some((r) => r.key !== key && r.product?._id === product._id)
    ) {
      toast.warning("Product already added");
      return false;
    }
    const sellingPrice = Number(product.sellingPrice || product.mrp || 0);
    updateRow(key, {
      product,
      productLabel: `${product.name} (${product.sku || product.productId})`,
      productId: product._id || "",
      variantId: "",
      name: product.name || "",
      mrp: Number(product.mrp || sellingPrice || 0),
      sellingPrice,
      taxPercent: Number(product.gstRate ?? 18),
      quantity: 1,
      available: null,
    });
    if (product.productType !== "variant" && product._id) {
      fetchAvailability(key, product._id, undefined);
    }
  };

  const handleSelectVariant = (key: number, row: LineItemRow, variantId: string) => {
    if (
      rows.some(
        (r) => r.key !== key && r.product?._id === row.product?._id && r.variantId === variantId
      )
    ) {
      toast.warning("This variant is already added");
      return;
    }
    // Variant products carry their own price/MRP on each variant — the
    // root product's own sellingPrice/mrp is usually unset for these, so
    // pull pricing from the chosen variant once it's known.
    const variant = row.product?.variants?.find((v) => v._id === variantId);
    const sellingPrice = Number(
      variant?.sellingPrice || row.product?.sellingPrice || row.product?.mrp || 0
    );
    const mrp = Number(variant?.mrp || row.product?.mrp || sellingPrice || 0);
    updateRow(key, { variantId, sellingPrice, mrp });
    if (row.product?._id) fetchAvailability(key, row.product._id, variantId);
  };

  useEffect(() => {
    if (highlightedRowKey === null) return;
    const timer = setTimeout(() => setHighlightedRowKey(null), 1500);
    return () => clearTimeout(timer);
  }, [highlightedRowKey]);

  const applyScannedProduct = (product: Product, variantId: string) => {
    const normalizedVariantId = variantId || "";
    const existingIdx = rows.findIndex(
      (r) => r.product?._id === product._id && (r.variantId || "") === normalizedVariantId
    );

    if (existingIdx !== -1) {
      const existing = rows[existingIdx];
      updateRow(existing.key, { quantity: (Number(existing.quantity) || 0) + 1 });
      toast.success(`Increased quantity for ${product.name}`);
      setHighlightedRowKey(existing.key);
      return;
    }

    const variant = normalizedVariantId
      ? product.variants?.find((v) => v._id === normalizedVariantId)
      : undefined;
    const sellingPrice = Number(
      variant?.sellingPrice || product.sellingPrice || product.mrp || 0
    );
    const mrp = Number(variant?.mrp || product.mrp || sellingPrice || 0);
    const quantity = 1;
    const discountPercent = 0;
    const totalPrice = Number((quantity * sellingPrice * (1 - discountPercent / 100)).toFixed(2));

    const patch: Partial<LineItemRow> = {
      product,
      productLabel: `${product.name} (${product.sku || product.productId})`,
      productId: product._id || "",
      variantId: normalizedVariantId,
      name: product.name || "",
      mrp,
      sellingPrice,
      discountPercent,
      taxPercent: Number(product.gstRate ?? 18),
      quantity,
      totalPrice,
      available: null,
    };

    const lastRow = rows[rows.length - 1];
    let targetKey: number;
    if (lastRow && !lastRow.product) {
      targetKey = lastRow.key;
      updateRow(targetKey, patch);
    } else {
      const newRow: LineItemRow = { ...emptyRow(), ...patch };
      targetKey = newRow.key;
      setRows((prev) => [...prev, newRow]);
    }

    toast.success(`Added ${product.name}`);
    setHighlightedRowKey(targetKey);
    if (product._id) fetchAvailability(targetKey, product._id, normalizedVariantId || undefined);
  };

  // ------------------ Totals ------------------
  const discountPercentValue = parseFloat(discountPercentage) || 0;
  const subTotal = rows.reduce((sum, item) => sum + item.totalPrice, 0);
  const taxValue = rows.reduce(
    (sum, item) => sum + (item.totalPrice * (item.taxPercent || 0)) / 100,
    0
  );
  const discountAmount = (subTotal * discountPercentValue) / 100;
  const grandTotal = subTotal + taxValue - discountAmount;

  const rowsValid = rows.every(
    (r) =>
      r.product?._id &&
      (r.product.productType !== "variant" || r.variantId) &&
      Number(r.quantity) > 0 &&
      Number(r.sellingPrice) > 0 &&
      (r.available === null || Number(r.quantity) <= r.available)
  );
  const isValid = !!warehouseId && rows.length > 0 && rowsValid;

  // ------------------ Submit ------------------
  const handleSubmit = () => {
    setTouched(true);
    if (!warehouseId) {
      toast.error(`Please select a ${warehouseLabel.toLowerCase()}`);
      return;
    }
    if (!customer || !customer.name || !customer.mobile) {
      toast.error("Please select or add a customer");
      return;
    }
    if (!customer.addressLine1) {
      toast.error("Customer Address Line 1 is required");
      return;
    }
    if (!rowsValid) {
      toast.error(
        "Fill in every line item — product, variant (if any), a price, and a quantity within stock are all required"
      );
      return;
    }

    const payload = {
      warehouseId,
      orderDate,
      customer: customer!,
      products: rows.map((r) => ({
        productId: r.productId,
        ...(r.product?.productType === "variant" ? { variantId: r.variantId } : {}),
        quantity: r.quantity,
        mrp: r.mrp,
        sellingPrice: r.sellingPrice,
        discountPercent: r.discountPercent,
        taxPercent: r.taxPercent,
        totalPrice: r.totalPrice,
      })),
      taxAmount: taxValue,
      discount: discountAmount,
      paymentMode,
      remarks,
      createdBy: user?._id || "",
    };

    dispatch(
      saleId
        ? salesActions.editSalesOrderRequest({ id: saleId, data: payload as any })
        : salesActions.createSalesOrderRequest(payload as any)
    );
  };

  const pageTitle = saleId ? "Edit sale order" : "Create sale order";
  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  return (
    <DashboardLayout
      meta={{
        title: saleId ? "Edit Sale Order | Invyntra" : "Create Sale Order | Invyntra",
        description: "Create sales orders.",
        keywords: "sales orders, invoices, customers, invyntra",
      }}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <Receipt size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{pageTitle}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Reserve stock now, complete or cancel the order later.
            </p>
          </div>
        </div>

        {/* ---------------- WAREHOUSE & ORDER DATE ---------------- */}
        <Card>
          <div className="mb-4 flex items-center gap-2 border-b border-gray-100 pb-3 dark:border-gray-700">
            <span className="h-4 w-1 rounded-full bg-primary-500" />
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Fulfillment {warehouseLabel.toLowerCase()}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <FilterSelect
                variant="boxed"
                label={warehouseLabel}
                required
                value={warehouseId}
                onChange={setWarehouseId}
                allLabel={`Select ${warehouseLabel.toLowerCase()}`}
                options={warehouseOptions}
                error={touched && !warehouseId}
                disabled={isWarehouseLocked}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {isWarehouseLocked
                  ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                  : `Stock is reserved from this ${warehouseLabel.toLowerCase()} as soon as the order is created.`}
              </p>
            </div>
            <Input
              label="Order date"
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
            />
          </div>
        </Card>

        {/* ---------------- CUSTOMER INFO ---------------- */}
        <Card>
          <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Customer information
              </h2>
            </div>
            {customer && (
              <button
                type="button"
                className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                onClick={handleChangeCustomer}
              >
                Change customer
              </button>
            )}
          </div>

          <SearchInput<Customer>
            label="Customer"
            required
            placeholder="Search by name or mobile number..."
            value={customerLabel}
            onSearch={handleCustomerSearch}
            onSelect={handleSelectCustomer}
            onClear={handleChangeCustomer}
            renderOption={(option) => (
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-100">
                  {option.data.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {option.data.mobile}
                  {option.data.city ? ` · ${option.data.city}` : ""}
                </div>
              </div>
            )}
          />
          {touched && !customer && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              Please select or add a customer
            </p>
          )}

          <div className="mt-3">
            {customerSearching ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Fetching customers...
              </p>
            ) : customer ? (
              <div className="rounded-xl border border-gray-100 p-4 dark:border-gray-700">
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  <InfoRow label="Name" value={customer.name} />
                  <InfoRow label="Mobile" value={customer.mobile} />
                  <InfoRow label="Email" value={customer.email || "—"} />
                  <InfoRow label="GSTIN" value={customer.gstNumber || "—"} />
                  <InfoRow
                    label="Address"
                    value={
                      [
                        customer.addressLine1,
                        customer.addressLine2,
                        customer.city,
                        customer.state,
                        customer.pincode,
                      ]
                        .filter(Boolean)
                        .join(", ") || "—"
                    }
                    span
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-gray-200 p-4 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {customerSearched
                    ? "No matching customer found."
                    : "Search above to find an existing customer."}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddCustomerModal(true)}
                >
                  <Plus size={14} className="mr-1" />
                  Add Customer
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* ---------------- LINE ITEMS ---------------- */}
        <Card>
          <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-3 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Products</h2>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {rows.length}
              </span>
            </div>
            {!!warehouseId && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Scan barcode"
                  onClick={() => setScanOpen(true)}
                >
                  <ScanLine size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={addRow}>
                  <Plus size={14} className="mr-1" />
                  Add Product
                </Button>
              </div>
            )}
          </div>

          {!warehouseId ? (
            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              Pick a {warehouseLabel.toLowerCase()} above to search products and add line items.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[980px]">
                <div className="grid grid-cols-[36px_2fr_1.3fr_80px_100px_80px_120px_100px_100px_36px] gap-2 px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <span />
                  <span>Product</span>
                  <span>Variant</span>
                  <span>Qty</span>
                  <span className="flex items-center gap-1">
                    Price
                    <InfoIcon info="Selling price per unit charged to the customer for this line. Defaults from the product's selling price/MRP but can be edited per order." />
                  </span>
                  <span>Disc %</span>
                  <span className="flex items-center gap-1">
                    Available
                    <InfoIcon info={`On-hand quantity at the selected ${warehouseLabel.toLowerCase()}, minus stock already reserved by other pending sale orders. Quantity can't exceed this.`} />
                  </span>
                  <span>Tax</span>
                  <span>Total</span>
                  <span />
                </div>

                <div className="space-y-2">
                  {rows.map((row, idx) => {
                    const isVariantProduct = row.product?.productType === "variant";
                    const variantOptions = (row.product?.variants || []).map((v) => ({
                      label: formatVariantLabel(v.optionValues) || v.sku || "Variant",
                      value: v._id || "",
                      disabled: rows.some(
                        (r) =>
                          r.key !== row.key &&
                          r.product?._id === row.product?._id &&
                          r.variantId === v._id
                      ),
                    }));
                    const lineTax = (row.totalPrice * (row.taxPercent || 0)) / 100;
                    const overQty =
                      row.available !== null && Number(row.quantity) > row.available;
                    const rowInvalid =
                      touched &&
                      (!row.product?._id ||
                        (isVariantProduct && !row.variantId) ||
                        !(Number(row.quantity) > 0) ||
                        !(Number(row.sellingPrice) > 0) ||
                        overQty);

                    const isHighlighted = row.key === highlightedRowKey;

                    return (
                      <div
                        key={row.key}
                        className={`grid grid-cols-[36px_2fr_1.3fr_80px_100px_80px_120px_100px_100px_36px] items-center gap-2 rounded-xl border p-2 transition-colors ${
                          isHighlighted
                            ? "border-primary-300 bg-primary-50/60 ring-2 ring-primary-300 dark:border-primary-700 dark:bg-primary-900/20 dark:ring-primary-700"
                            : rowInvalid
                            ? "border-red-200 dark:border-red-900/60"
                            : "border-gray-100 dark:border-gray-700"
                        }`}
                      >
                        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 text-xs font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                          {idx + 1}
                        </span>

                        <SearchInput<Product>
                          placeholder="Search product..."
                          value={row.productLabel}
                          onSearch={async (query) => {
                            const res = await productApis.getProductList({
                              page: 1,
                              limit: 10,
                              status: "active",
                              search: query,
                            });
                            const list: Product[] = res?.data?.data || res?.data || [];
                            return list.map((p) => ({
                              value: p._id || "",
                              label: `${p.name} (${p.sku || p.productId})`,
                              data: p,
                            }));
                          }}
                          onSelect={(option: SearchInputOption<Product>) =>
                            handleSelectProduct(row.key, option.data)
                          }
                          onClear={() =>
                            updateRow(row.key, {
                              product: null,
                              productLabel: "",
                              productId: "",
                              variantId: "",
                              available: null,
                            })
                          }
                          renderOption={(option) => (
                            <div>
                              <div className="font-medium text-gray-800 dark:text-gray-100">
                                {option.data.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                SKU: {option.data.sku || "-"} · HSN:{" "}
                                {option.data.hsnCode || "-"}
                              </div>
                            </div>
                          )}
                        />

                        {isVariantProduct ? (
                          <div className="[&_label]:hidden">
                            <FilterSelect
                              variant="boxed"
                              label="Variant"
                              value={row.variantId || ""}
                              onChange={(val) => handleSelectVariant(row.key, row, val)}
                              allLabel="Select"
                              options={variantOptions}
                              error={touched && !row.variantId}
                            />
                          </div>
                        ) : (
                          <span className="text-center text-sm text-gray-400 dark:text-gray-500">-</span>
                        )}

                        <Input
                          type="text"
                          inputClassName="text-center"
                          value={row.quantity}
                          error={overQty}
                          onChange={(e) => updateRow(row.key, { quantity: e.target.value as any })}
                        />
                        <Input
                          type="text"
                          inputClassName="text-center"
                          value={row.sellingPrice}
                          onChange={(e) =>
                            updateRow(row.key, { sellingPrice: Number(e.target.value) || 0 })
                          }
                        />
                        <Input
                          type="text"
                          inputClassName="text-center"
                          value={row.discountPercent}
                          onChange={(e) =>
                            updateRow(row.key, { discountPercent: parseFloat(e.target.value) || 0 })
                          }
                        />

                        <span
                          className={`text-center text-sm ${
                            overQty
                              ? "font-semibold text-red-600 dark:text-red-400"
                              : "text-gray-600 dark:text-gray-300"
                          }`}
                        >
                          {row.product ? (row.available === null ? "…" : row.available) : "-"}
                        </span>
                        <span className="text-center text-sm text-gray-600 dark:text-gray-300">
                          {currencyFormatter(lineTax)}
                        </span>
                        <span className="text-center text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {currencyFormatter(row.totalPrice + lineTax)}
                        </span>

                        <button
                          type="button"
                          onClick={() => removeRow(row.key)}
                          disabled={rows.length === 1}
                          className="flex h-7 w-7 items-center justify-center rounded-md text-red-600 hover:bg-red-50 disabled:opacity-30 dark:text-red-400 dark:hover:bg-red-900/30"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* ---------------- PAYMENT SUMMARY ---------------- */}
        <Card className="space-y-3">
          <div className="mb-1 flex items-center gap-2 border-b border-gray-100 pb-3 dark:border-gray-700">
            <span className="h-4 w-1 rounded-full bg-primary-500" />
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Payment summary
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Input label="Tax" value={currencyFormatter(taxValue)} readOnly />
            <Input
              label="Discount (%)"
              type="number"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
              placeholder="Enter discount %"
            />
            <Dropdown
              label="Payment Mode"
              value={paymentMode}
              options={[
                { label: "Cash", value: "cash" },
                { label: "Online", value: "online" },
                // Orders saved before Cash/Online keep showing their old mode.
                ...(["cash", "online"].includes(paymentMode)
                  ? []
                  : [{ label: paymentMode.toUpperCase(), value: paymentMode }]),
              ]}
              onChange={(val) => setPaymentMode(val || "cash")}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {saleId
              ? "Changing the mode here doesn't record a payment — collect it from the order page."
              : paymentMode === "online"
              ? "The order is saved, then the payment window opens (UPI, cards, netbanking) — or record it offline."
              : "Recorded as paid in full in cash when the order is placed."}
          </p>

          <Textarea
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Additional notes..."
          />

          <div className="flex justify-end border-t border-gray-200 pt-3 mt-2 dark:border-gray-700">
            <table>
              <tbody>
                <tr>
                  <td className="py-0.5 text-sm text-gray-700 dark:text-gray-300">Subtotal:</td>
                  <td className="py-0.5 text-sm text-right text-gray-700 dark:text-gray-300">
                    {currencyFormatter(subTotal)}
                  </td>
                </tr>
                <tr>
                  <td className="py-0.5 text-sm text-gray-700 dark:text-gray-300">Tax:</td>
                  <td className="py-0.5 text-sm text-right text-gray-700 dark:text-gray-300">
                    {currencyFormatter(taxValue)}
                  </td>
                </tr>
                <tr>
                  <td className="py-0.5 text-sm text-gray-700 dark:text-gray-300">
                    Discount ({discountPercentValue}%):
                  </td>
                  <td className="py-0.5 text-sm text-right text-gray-700 dark:text-gray-300">
                    {currencyFormatter(discountAmount)}
                  </td>
                </tr>
                <tr>
                  <td className="py-0.5 font-semibold text-gray-900 text-lg dark:text-gray-100">
                    Grand Total:
                  </td>
                  <td className="text-right font-semibold text-gray-900 text-lg pl-4 dark:text-gray-100">
                    {currencyFormatter(grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>

        {/* ---------------- ACTION BUTTONS ---------------- */}
        <div className="flex justify-end space-x-3 pb-2">
          <Button onClick={handleClose} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={createSaleOrder.loading || editSaleOrder.loading || isLoadingDetails}
            disabled={isLoadingDetails || (touched && !isValid)}
          >
            {saleId
              ? "Save changes"
              : paymentMode === "online"
              ? "Collect payment"
              : "Place order"}
          </Button>
        </div>
      </div>

      {checkoutOrder && (
        <CheckoutPaymentModal
          open={!!checkoutOrder}
          order={checkoutOrder}
          onPaid={({ amount, balanceDue }) => {
            toast.success(
              balanceDue > 0
                ? `${currencyFormatter(amount)} received for ${checkoutOrder.orderId} — ${currencyFormatter(balanceDue)} still due`
                : `Payment of ${currencyFormatter(amount)} received — ${checkoutOrder.orderId} is paid`
            );
            setCheckoutOrder(null);
            handleClose();
          }}
          onClose={() => {
            // The order is already saved — payment (or a link sent from the
            // modal) is tracked on the order page.
            toast.info(
              `${checkoutOrder.orderId} saved — payment status is on the order page`
            );
            setCheckoutOrder(null);
            handleClose();
          }}
        />
      )}

      {showAddCustomerModal && (
        <AddCustomerModal
          open={showAddCustomerModal}
          onClose={() => setShowAddCustomerModal(false)}
          onSuccess={handleCustomerCreated}
        />
      )}

      <ScanProductModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onProductFound={applyScannedProduct}
      />
    </DashboardLayout>
  );
};

export default SalesForm;
